import React, { createContext, useContext, useReducer, ReactNode } from 'react';

// Action types
export const SELECT_FOLDER = 'SELECT_FOLDER';
export const SELECT_FILE = 'SELECT_FILE';
export const SET_SEARCH_TERM = 'SET_SEARCH_TERM';
export const SET_TIME_FILTER = 'SET_TIME_FILTER';
export const SET_VIEW_TYPE = 'SET_VIEW_TYPE';
export const SET_FILE_MANAGER_STATE = 'SET_FILE_MANAGER_STATE';

// Types
export interface FileType {
  id: string;
  name: string;
  path: string;
  extension?: string;
  size?: number;
  lastModified?: Date;
}

export interface FolderType {
  id: string;
  name: string;
  path: string;
  type: 'directory';
}

export interface FileManagerState {
  selectedFolder: string | null;
  selectedFile: FileType | null;
  expandedFolders: string[];
  searchTerm: string;
  timeFilter: string;
  viewType: 'list' | 'grid';
  isUploadModalOpen: boolean;
  isDetailsModalOpen: boolean;
  isNewFolderModalOpen: boolean;
  refreshTimestamp: number;
}

interface FileManagerContextType {
  fileManagerState: FileManagerState;
  fileManagerDispatch: React.Dispatch<FileManagerAction>;
}

type FileManagerAction = 
  | { type: typeof SELECT_FOLDER; payload: string }
  | { type: typeof SELECT_FILE; payload: FileType }
  | { type: typeof SET_SEARCH_TERM; payload: string }
  | { type: typeof SET_TIME_FILTER; payload: string }
  | { type: typeof SET_VIEW_TYPE; payload: 'list' | 'grid' }
  | { type: typeof SET_FILE_MANAGER_STATE; payload: Partial<FileManagerState> };

// Initial state
const initialState: FileManagerState = {
  selectedFolder: null,
  selectedFile: null,
  expandedFolders: ['root'],
  searchTerm: '',
  timeFilter: 'All time',
  viewType: 'list',
  isUploadModalOpen: false,
  isDetailsModalOpen: false,
  isNewFolderModalOpen: false,
  refreshTimestamp: Date.now()
};

// Reducer
const fileManagerReducer = (state: FileManagerState, action: FileManagerAction): FileManagerState => {
  switch (action.type) {
    case SELECT_FOLDER:
      return {
        ...state,
        selectedFolder: action.payload
      };
    case SELECT_FILE:
      return {
        ...state,
        selectedFile: action.payload,
        isDetailsModalOpen: true
      };
    case SET_SEARCH_TERM:
      return {
        ...state,
        searchTerm: action.payload
      };
    case SET_TIME_FILTER:
      return {
        ...state,
        timeFilter: action.payload
      };
    case SET_VIEW_TYPE:
      return {
        ...state,
        viewType: action.payload
      };
    case SET_FILE_MANAGER_STATE:
      return {
        ...state,
        ...action.payload
      };
    default:
      return state;
  }
};

// Context
const FileManagerContext = createContext<FileManagerContextType | undefined>(undefined);

// Provider props
interface FileManagerProviderProps {
  children: ReactNode;
}

// Provider
const FileManagerProvider: React.FC<FileManagerProviderProps> = ({ children }) => {
  const [fileManagerState, fileManagerDispatch] = useReducer(
    fileManagerReducer,
    initialState
  );

  return (
    <FileManagerContext.Provider value={{ fileManagerState, fileManagerDispatch }}>
      {children}
    </FileManagerContext.Provider>
  );
};

// Custom hook to use the context
export const useFileManagerContext = (): FileManagerContextType => {
  const context = useContext(FileManagerContext);
  if (!context) {
    throw new Error('useFileManagerContext must be used within a FileManagerProvider');
  }
  return context;
};

export default FileManagerProvider;
